<template>
  <slot
    :isLoading="showInitialSkeleton"
    :hasError="hasError"
    :totalOpenInterest="totalOpenInterest"
    :formattedValue="totalOpenInterest !== null ? formatNumber(totalOpenInterest) : '&mdash;'"
  >
    <div class="text-on-surface">
      <Skeleton v-if="showInitialSkeleton" height="1rem" width="40px" animation="none" />
      <span v-else-if="hasError">-</span>
      <span v-else-if="totalOpenInterest !== null">{{ formatNumber(totalOpenInterest) }}</span>
      <span v-else class="text-on-surface">&mdash;</span>
    </div>
  </slot>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { formatCompactNumber } from '@/utils/formatNumber';
import { useOptionOpenInterest } from '@/api/mdfarm.queries';
import { useOpenInterestStream } from '@/composables/useOpenInterestStream';
import { useUserState } from '@/stores/useUserState';

interface StrikeOpenInterestProps {
  conid_yes: number;
  conid_no: number;
  exchange: string;
  prefix?: boolean;
  seedOpenInterest?: number;
}
const props = defineProps<StrikeOpenInterestProps>();

const userState = useUserState();
const useWs = computed(() => !userState.usePublicApi);
const useSeed = computed(() => props.seedOpenInterest !== undefined);

// WS stream (auth only)
const stream = useOpenInterestStream(
  computed(() => (useWs.value ? props.conid_yes : undefined)),
  computed(() => props.exchange),
);

const hasLiveInterest = computed(
  () => useWs.value && (stream.putInterest.value !== null || stream.callInterest.value !== null),
);
const useRest = computed(() => !useSeed.value && !hasLiveInterest.value);

const query = useOptionOpenInterest(
  computed(() => (useRest.value ? props.conid_yes : undefined)),
  computed(() => (useRest.value ? props.exchange : undefined)),
);

const showInitialSkeleton = computed(() => useRest.value && query.isFetching.value && !query.data.value);
const hasError = computed(() => useRest.value && query.isError.value);

const totalOpenInterest = computed(() => {
  if (hasLiveInterest.value) {
    return (stream.putInterest.value ?? 0) + (stream.callInterest.value ?? 0);
  }
  if (useSeed.value) return props.seedOpenInterest!;
  const data = query.data.value;
  if (!data) return null;
  return data.putInterest + data.callInterest;
});

const formatNumber = formatCompactNumber;
</script>
